/* ------------------------ Functional imports start ------------------------ */
import LogTool from '../../../logger/logTools'
import {
  createRequest as createRequestModel,
  inputToAPIRequestJSON as inputToAPIRequestJSONInternal,
  updateOfferApi,
  uploadOfferFile,
} from '../api/requestApi'
import { type Offer } from './types'

import { parsePriceString } from '../../UnauthRequest/utils/functions'
import { type Article, type Part } from '../../Item/utils/types'
import { type Request } from '../utils/types'
/* ------------------------- Functional imports end ------------------------- */

const log = new LogTool({ context: 'RequestFunctions', enable: true, logLevel: 'warn' })

export { createRequestModel as createRequest }
export { inputToAPIRequestJSONInternal as inputToAPIRequestJSON }

export const parseNumericPrice = (value: string | number | undefined): number | null => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null
  }
  if (!value) return null
  const trimmed = value.trim()
  if (trimmed === '' || trimmed.toLowerCase() === 'loading') return null
  if (trimmed.includes('€')) {
    try {
      return parsePriceString(trimmed).value
    } catch {
      // Fallback to generic parsing below.
    }
  }
  const match = trimmed.match(/-?[\d,.]+/)
  if (!match) return null
  const numericPart = match[0]
  const normalized = numericPart.includes(',')
    ? numericPart.replace(/\./g, '').replace(',', '.')
    : numericPart.replace(/,/g, '')
  const parsed = Number.parseFloat(normalized)
  return Number.isFinite(parsed) ? parsed : null
}

export const getEstimatedPriceFromArticle = (article: Article | Part): number | null => {
  const requests: string | Request[] | undefined = article?.requests
  if (!requests) return null
  if (!Array.isArray(requests) || requests.length === 0) return null
  
  // Try to find a request that matches this article
  // Handle both cases: r.article as string URL or as object
  let articleRequest = requests.find(r => {
    const requestArticle = r.article
    if (typeof requestArticle === 'string') {
      return requestArticle === article.self
    }
    if (typeof requestArticle === 'object' && requestArticle !== null) {
      return requestArticle.self === article.self
    }
    return false
  })
  
  // If no exact match found, try to use the first request with calculatedCosts
  // This is useful for parts where the request might not have article matching
  if (!articleRequest) {
    articleRequest = requests.find(r => r.calculatedCosts && Object.keys(r.calculatedCosts).length > 0)
  }
  
  // If still no request found and there's only one request, use it
  if (!articleRequest && requests.length === 1) {
    articleRequest = requests[0]
  }
  
  const calculatedCosts: { [key: string]: any } | undefined = articleRequest?.calculatedCosts
  if (!calculatedCosts) return null
  
  // Try to get price for quantity 1 first, then fall back to first available key
  const price = calculatedCosts[1] ?? calculatedCosts['1'] ?? Object.values(calculatedCosts)[0]
  if (!price) return null
  return parseNumericPrice(price)
}

export const hasLetters = (value: string): boolean => /[A-Za-z]/.test(value)

/**
 * Returns the first non-temporary request that is directly associated with this specific article/part.
 * Used to detect if a part has already been requested by a non-guest user.
 * 
 * Note: This checks that the request's article matches the current article's self URL.
 * If a part is included in a BOM request but not requested directly, this will return null,
 * allowing the user to still create a separate request for the individual part.
 */
export const getExistingNonTempRequest = (article: Article | Part | null | undefined): Request | null => {
  if (!article) return null
  const requests = article.requests
  if (!requests || !Array.isArray(requests) || requests.length === 0) return null

  const articleSelf = article.self

  // Find first non-temporary request that is specifically for this article
  const nonTempRequest = requests.find((r): r is Request => {
    if (typeof r !== 'object' || r === null || r.is_temp !== false) {
      return false
    }
    
    // Check if the request's article matches this article
    const requestArticle = r.article
    if (typeof requestArticle === 'string') {
      return requestArticle === articleSelf
    }
    if (typeof requestArticle === 'object' && requestArticle !== null) {
      return requestArticle.self === articleSelf
    }
    return false
  })
  
  return nonTempRequest ?? null
}

export const isSamePrice = (left: number | null, right: number | null): boolean => {
  if (left === null || right === null) return false
  return Math.abs(left - right) < 0.005
}

async function postOfferFiles(files: any[], offer: Offer): Promise<boolean> {
  if (!files || files.length === 0) {
    return true
  }

  const results = await Promise.all(files.map(file => handleOfferFileUpload(file, offer)))
  return results.every(result => result)
}

async function handleOfferFileUpload(file: any, offer: Offer): Promise<boolean> {
  log.info('Begin posting offer file')

  try {
    await uploadOfferFile(offer.self, file as File)
    return true
  } catch (error) {
    log.error('Error posting offer file', error)
    return false
  }
}

/**
 * @deprecated Prefer using the React Query hook `useUpdateOffer`.
 */
export async function updateOffer(
  o: Offer,
  files: any[],
  onSuccess: (offer: Offer) => void,
  onError: (error: any) => void
): Promise<void> {
  log.info('Begin updating offer')

  try {
    const updatedOffer = await updateOfferApi(o.self, o)

    const success = await postOfferFiles(files, updatedOffer)
    if (!success) {
      throw new Error('Error posting offer files')
    }

    onSuccess(updatedOffer)
  } catch (error) {
    log.error('Error updating offer', error)
    onError(error)
  }
}
